import React, { useState, useMemo } from 'react';

const TicketInventory = ({ tickets = [], loading }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("attendee"); // attendee, event, status
  const [sortOrder, setSortOrder] = useState("asc");

  const sortedAndFilteredTickets = useMemo(() => {
    let filtered = tickets.filter((ticket) => {
      const firstName = (ticket.firstName || "").toLowerCase();
      const lastName = (ticket.lastName || "").toLowerCase();
      const email = (ticket.email || "").toLowerCase();
      const eventTitle = (ticket.eventId?.title || "").toLowerCase();
      const status = (ticket.status || "confirmed").toLowerCase();
      
      const matchesSearch = 
        firstName.includes(searchTerm.toLowerCase()) ||
        lastName.includes(searchTerm.toLowerCase()) ||
        email.includes(searchTerm.toLowerCase()) ||
        eventTitle.includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || status.includes(statusFilter.toLowerCase());
      
      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "attendee") {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === "event") {
        const titleA = (a.eventId?.title || "").toLowerCase();
        const titleB = (b.eventId?.title || "").toLowerCase();
        comparison = titleA.localeCompare(titleB);
      } else if (sortBy === "status") {
        comparison = (a.status || "confirmed").localeCompare(b.status || "confirmed");
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [tickets, searchTerm, statusFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="tickets-section mt-4">
      {/* Filters & Table Card */}
      <div className="modern-card">
        {/* Filters Bar */}
        <div className="modern-filter-bar">
          <div className="d-flex flex-column flex-md-row gap-4">
            {/* Search */}
            <div className="modern-search-wrapper w-100">
              <i className="isax isax-search-normal-1" />
              <input
                type="text"
                className="modern-search-input"
                placeholder="Search by Attendee or Ticket ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="d-flex flex-wrap align-items-center gap-3">
              {/* Event Filter (Example placeholder) */}
              <div className="modern-select-wrapper">
                <i className="isax isax-ticket prefix-icon" />
                <select className="modern-select">
                  <option value="all">All Events</option>
                </select>
                <i className="isax isax-arrow-down-1 suffix-icon" />
              </div>

              {/* Status Filter */}
              <div className="modern-select-wrapper">
                <i className="isax isax-filter prefix-icon" />
                <select 
                  className="modern-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <i className="isax isax-arrow-down-1 suffix-icon" />
              </div>

              {/* Results count */}
              <div className="results-badge">
                {loading ? (
                  <span>Loading...</span>
                ) : (
                  <>
                    <span className="fw-bold text-dark me-1">
                      {sortedAndFilteredTickets.length}
                    </span>
                    <span>tickets</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table Header */}
        <div className="modern-table-header flex-column flex-md-row align-items-start align-items-md-center">
          <h2 className="fw-bold mb-3 mb-md-0">Ticket Inventory ({sortedAndFilteredTickets.length})</h2>
          <div className="d-flex flex-wrap align-items-center gap-3 gap-md-4">
            <span className="text-muted small">Sort by:</span>
            <div className="d-flex gap-2">
              <button 
                className={`sort-btn ${sortBy === 'attendee' ? 'active' : ''}`}
                onClick={() => handleSort('attendee')}
              >
                Attendee {sortBy === 'attendee' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'event' ? 'active' : ''}`}
                onClick={() => handleSort('event')}
              >
                Event {sortBy === 'event' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'status' ? 'active' : ''}`}
                onClick={() => handleSort('status')}
              >
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted fw-medium">Loading inventory...</p>
          </div>
        ) : sortedAndFilteredTickets.length > 0 ? (
          <div className="table-responsive">
            <table className="table modern-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Attendee</th>
                  <th>Event / Tier</th>
                  <th className="text-center">QR Code</th>
                  <th className="text-center">Status</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredTickets.map((ticket) => (
                  <tr key={ticket._id || ticket.id}>
                    {/* Attendee Info */}
                    <td>
                      <div className="d-flex flex-column">
                        <p className="fw-bold text-dark mb-0">
                          {ticket.firstName} {ticket.lastName}
                        </p>
                        <p className="text-muted small mb-0">
                          {ticket.email}
                        </p>
                      </div>
                    </td>

                    {/* Event / Tier */}
                    <td>
                      <div className="d-flex flex-column">
                        <span className="fw-medium text-dark small">
                          {ticket.eventId?.title || "Unknown Event"}
                        </span>
                        <span className="badge bg-light text-muted border extra-small align-self-start mt-1">
                          {ticket.ticketType}
                        </span>
                      </div>
                    </td>

                    {/* QR Code */}
                    <td className="text-center">
                      {ticket.qrCodeImage ? (
                        <div
                          className="qr-thumb mx-auto pointer"
                          onClick={() => {
                            /* Option for full screen view */
                          }}
                        >
                          <img
                            src={ticket.qrCodeImage}
                            alt="QR"
                            className="img-fluid rounded shadow-sm"
                            style={{
                              width: "36px",
                              height: "36px",
                              border: "1px solid #f1f5f9",
                            }}
                          />
                        </div>
                      ) : (
                        <i className="isax isax-barcode text-muted opacity-50"></i>
                      )}
                    </td>

                    {/* Status */}
                    <td className="text-center">
                      <span
                        className={`status-badge ${(ticket.status || "confirmed").toLowerCase()}`}
                      >
                        {ticket.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="d-flex justify-content-end gap-1">
                        <button
                          className="modern-action-btn view"
                          title="View Ticket"
                        >
                          <i className="isax isax-ticket-2"></i>
                        </button>
                        <button
                          className="modern-action-btn edit"
                          title="Download"
                        >
                          <i className="isax isax-import"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-5 text-center bg-light bg-opacity-25">
            <div
              className="mx-auto bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-4"
              style={{ width: "80px", height: "80px" }}
            >
              <i
                className="isax isax-ticket text-primary"
                style={{ fontSize: "40px" }}
              ></i>
            </div>
            <h3 className="fw-bold text-dark mb-2">No Tickets Found</h3>
            <p className="text-muted mb-0">
              You don't have any tickets sold for your events yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketInventory;
